import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "width"];
import PropTypes from 'prop-types';
import { useContext, useState, memo } from 'react';
import classNames from 'classnames';
import styled, { css } from 'styled-components';
import { BATTLESHIP, OLAF, THUNDERDOME } from 'HubStyleTokens/colors';
import { getSide, getEdge } from '../utils/Placement';
import { USE_CLASSES } from '../utils/Use';
import { OpenPopupContext } from '../../context/internal/OpenPopupContext';
import { useSubscribe } from '../../hooks/pubSub';
import { toPx } from '../../utils/Styles';
import { Metrics } from '../../utils/Metrics';

/**
 * @internal
 * **Do not use.** Ask about your use case in #hds-support.
 *
 * Arrow size strings used internally by `UIPopoverArrow`-reliant components.
 */

/** @internal */

/** @internal */

/** @internal */

/** @internal  */
import { jsx as _jsx } from "react/jsx-runtime";
const mediumArrowSize = 16;
const smallArrowSize = 12;
const isShepherdArrowWhite = (placement, headerHeight, inset, arrowWidth = mediumArrowSize) => {
  // If we have an inset on the horizontal sides, we can't trust the default placement as truth
  const sidePlacement = getSide(placement);
  if (inset && (sidePlacement === 'left' || sidePlacement === 'right')) {
    if (headerHeight) {
      return inset + arrowWidth / 2 > headerHeight;
    } // if no valid headerHeight, ignore and default to base calc;
  }
  return sidePlacement !== 'bottom' && getEdge(placement) !== 'bottom';
};
const adjustInsetForShepherdArrows = (placement, headerHeight, inset, arrowWidth = mediumArrowSize) => {
  const fixedOffset = Math.sqrt(arrowWidth);
  const sidePlacement = getSide(placement);
  if (inset && (sidePlacement === 'left' || sidePlacement === 'right')) {
    if (headerHeight) {
      /**
       * If header height is known, avoid placements which cause the arrow to span both the purple and white background
       */
      const hypotenuse = Math.sqrt(2 * (arrowWidth * arrowWidth));
      const arrowMidPointOffset = inset + hypotenuse / 2;
      // Arrow within the header (purple area), but partially overlaps the header-body border
      if (arrowMidPointOffset <= headerHeight && arrowMidPointOffset > headerHeight - hypotenuse / 2 + fixedOffset) {
        return headerHeight - hypotenuse + 4;
      }
      // Arrow below the header (white area), but partially overlaps the header-body border
      if (arrowMidPointOffset > headerHeight && arrowMidPointOffset <= headerHeight + hypotenuse / 2 + fixedOffset) {
        return headerHeight + 4;
      }
    } // if no valid headerHeight, ignore and treat like default.
  }
  // Otherwise return the original inset, no adjustments made.
  return inset;
};
const getBackgroundColor = ({
  color,
  headerHeight,
  inset,
  placement = 'top',
  popoverUse,
  width
}) => {
  if (color) {
    return color;
  }
  if (popoverUse === 'shepherd') {
    return isShepherdArrowWhite(placement, headerHeight, inset, width) ? OLAF : THUNDERDOME;
  }
  return 'inherit';
};
const getSideStyles = ({
  placement = 'top',
  width
}) => {
  switch (getSide(placement)) {
    case 'top':
      // Arrow points down
      return css(["transform:rotate(45deg);top:calc(100% - ", "px);"], width / 2);
    case 'right':
      // Arrow points left
      return css(["transform:rotate(135deg);left:-", "px;"], width / 2);
    case 'bottom':
      // Arrow points up
      return css(["transform:rotate(-135deg);top:-", "px;"], width / 2);
    case 'left':
      // Arrow points right
      return css(["transform:rotate(-45deg);left:calc(100% - ", "px);"], width / 2);
    default:
      return '';
  }
};
const getEdgeStyles = ({
  inset,
  placement = 'top'
}) => {
  switch (getEdge(placement)) {
    case 'top':
    case 'bottom':
    case 'middle':
      return css(["top:", "px;"], inset);
    case 'left':
    case 'right':
    case 'center':
      return css(["left:", "px;"], inset);
    default:
      return '';
  }
};
const getBorderUseStyles = ({
  headerHeight,
  inset,
  placement = 'top',
  popoverUse,
  width
}) => {
  if (popoverUse === 'default' || popoverUse === 'longform' || popoverUse === 'shepherd' && isShepherdArrowWhite(placement, headerHeight, inset, width)) {
    return `1px solid ${BATTLESHIP}`;
  }
  return null;
};
const Arrow = styled.div.withConfig({
  displayName: "UIPopoverArrow__Arrow",
  componentId: "sc-19txw9-0"
})(["position:absolute;pointer-events:none;border:", ";clip-path:polygon(100% 100%,0 100%,100% 0);border-top-left-radius:100%;border-top-color:transparent !important;border-left-color:transparent !important;border-bottom-right-radius:", ";width:", ";height:", ";background-color:", ";", ";", ";.tether-pinned &{display:none;}"], getBorderUseStyles, ({
  popoverUse
}) => popoverUse && popoverUse.includes('tooltip') ? '3px' : null, ({
  width
}) => toPx(width), ({
  width
}) => toPx(width), getBackgroundColor, getSideStyles, getEdgeStyles);
const UIPopoverArrow = /*#__PURE__*/memo(_ref => {
  let {
      className,
      width
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    positionChannel
  } = useContext(OpenPopupContext);
  const [positionConfig, setPositionConfig] = useState();
  if (positionChannel) {
    // Ticket filed here to fix this issue: https://git.hubteam.com/HubSpot/UIComponents/issues/11736
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useSubscribe(positionChannel, positionUpdate => {
      try {
        setPositionConfig(positionUpdate);
      } catch (error) {
        Metrics.counter('maximum-update-depth-exceeded', {
          component_name: 'UIPopoverArrow',
          error_message: error.message
        }).increment();
      }
    });
  }
  if (!positionConfig) return null;
  const {
    arrowInset,
    pinning,
    placement
  } = positionConfig;
  if (!!pinning || width === 0) return null;
  let computedArrowInset = arrowInset;
  const {
    headerHeight,
    popoverUse
  } = rest;
  if (popoverUse === 'shepherd') {
    const adjustedInsetForShepherdArrows = adjustInsetForShepherdArrows(placement, headerHeight, arrowInset, width);
    if (adjustedInsetForShepherdArrows) {
      computedArrowInset = adjustedInsetForShepherdArrows;
    }
  }
  return /*#__PURE__*/_jsx(Arrow, Object.assign({}, rest, {
    className: classNames('uiPopoverArrow private-popover__arrow', className),
    "data-popover-arrow": true,
    inset: computedArrowInset,
    placement: placement,
    width: width
  }));
});
if (process.env.NODE_ENV !== 'production') {
  const propTypes = {
    color: PropTypes.string,
    headerHeight: PropTypes.number,
    popoverUse: PropTypes.oneOf(Object.keys(USE_CLASSES)),
    width: PropTypes.number
  };
  UIPopoverArrow.propTypes = propTypes;
}
const defaultProps = {
  width: 20
};
/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
UIPopoverArrow.defaultProps = defaultProps;
UIPopoverArrow.displayName = 'UIPopoverArrow';
export const ARROW_SIZES = {
  none: {
    distance: 4,
    width: 0
  },
  medium: {
    distance: mediumArrowSize,
    width: 20
  },
  small: {
    distance: smallArrowSize,
    width: 16
  }
};
UIPopoverArrow.ARROW_SIZES = ARROW_SIZES;
export default UIPopoverArrow;